import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";

const CREATE_PRACTICE_CATEGORY = gql`
  mutation CreatePracticeCategory($input: PracticeCategoryCreateInput!) {
    practiceCategoryCreate(input: $input) {
      clientMutationId
      practiceCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

const GET_PROFILE = gql`
  query GetPracticeCategory($id: ID!) {
    practiceCategory(id: $id) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdatePracticeCategory($id: ID!, $name: String!) {
    practiceCategoryUpdate(
      input: { id: $id, practiceCategoryInput: { name: $name } }
    ) {
      clientMutationId
      practiceCategory {
        id
        name
        createdAt
        updatedAt
      }
    }
  }
`;

function ProfileAction({ isUpdate = false }) {
  const { user } = useAuthSession() as any;
  const notify = useNotify();
  const navigate = useNavigate();
  const { translator } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const profile_id = searchParams.get("id");

  const { data: profileData } = useQuery(GET_PROFILE, {
    variables: { id: profile_id },
    skip: !profile_id,
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createProfile, { loading, error }] = useMutation(
    CREATE_PRACTICE_CATEGORY,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateProfile] = useMutation(UPDATE_PROFILE, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!profileData?.practiceCategory) return undefined;

    const { name } = profileData?.practiceCategory;

    return {
      profile_name: name,
    };
  }, [profileData]);

  const form = useForm<any>({
    defaultValues: {
      profile_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateProfile({
          variables: {
            id: Number(profile_id),
            name: data?.profile_name,
          },
        });
        if (response.data) {
          notify.success(`Profile Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/profiles");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    const response = await createProfile({
      variables: {
        input: {
          practiceCategoryInput: { name: data?.profile_name },
        },
      },
    });
    try {
      if (actionType === "save") {
        if (response.data) {
          notify.success(`Profile Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/profiles");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`Profile Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {}
  }, []);

  return (
    <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
      <Grid
        container
        px={4}
        sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
        spacing={2}
      >
        <Grid item xs={12} sm={6} lg={6}>
          <FormControl required disabled={false} fullWidth margin="normal">
            <FormLabel>
              {translator("generic.Profile")}{" "}
              {translator("dashboardTableHead.Name")}
            </FormLabel>
            <OutlinedInput
              {...form?.register("profile_name", {
                required: "Required!",
              })}
            />
            {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
          </FormControl>
        </Grid>
      </Grid>
      <Stack
        sx={{ backgroundColor: "#ffffff" }}
        direction={"row"}
        justifyContent={"flex-end"}
        gap={2}
        px={4}
        py={4}
      >
        <Link
          to="/admin/profiles"
          onClick={() => form?.reset()}
          style={{
            backgroundColor: "#e0e0e0",
            padding: "8px 12px",
            color: "#989C9F",
            borderRadius: "10px",
            fontSize: "14px",
            textDecoration: "none",
          }}
        >
          {translator("Cancel")}
        </Link>
        <Button
          type="button"
          onClick={form.handleSubmit((data) =>
            handleFinish(data, isUpdate ? "update" : "save")
          )}
          style={{
            backgroundColor: "#00AAB0",
            padding: "8px 12px",
            color: "white",
            borderRadius: "10px",
            fontSize: "14px",
            textDecoration: "none",
          }}
        >
          {isUpdate ? translator("generic.Update") : translator("generic.Save")}
        </Button>
        <ConditionalRenderer condition={!isUpdate}>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, "saveAndAddNew")
            )}
            style={{
              backgroundColor: "#F6A000",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("generic.Save")} & {translator("generic.Add New")}
          </Button>
        </ConditionalRenderer>
      </Stack>
    </form>
  );
}

export { ProfileAction };
